import Text from 'components/Text/Text';
import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import { createAppError } from 'utils/helpers';
import { useTranslation } from 'react-i18next';
import { AppContext, IAppContext } from 'appState/appContext';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import style from './UploaderAndDownloader.module.scss';

interface IJSONUploader {
	onFileLoad: (jsonString: string, fullFile: null | File) => void;
	id: string;
	label: string;
	icon?: React.ReactNode;
	isValidJson?: boolean;
	requiredValidationMessage?: string;
	needDeleteFile?: boolean;
	width?: number;
}

const JSONUploader: React.FC<IJSONUploader> = ({
	onFileLoad,
	id,
	label,
	icon = (
		<svg className={style.icon}>
			<use xlinkHref={`${breadcrumbs}#uploadArrow`} />
		</svg>
	),
	isValidJson = true,
	requiredValidationMessage,
	needDeleteFile,
	width = 350,
}) => {
	const { addAppError } = useContext<IAppContext>(AppContext);
	const [file, setFile] = useState<string>('');
	const [fileName, setFileName] = useState<string>('');
	const [fullFile, setFullFile] = useState<null | File>(null);
	const { t } = useTranslation('error');

	useEffect(() => {
		if (file) {
			onFileLoad(file, fullFile);
			setFile('');
			setFullFile(null);
		}
	}, [file]);

	useEffect(() => {
		if (!isValidJson) {
			setFileName('');
			setFile('');
			setFullFile(null);
		}
	}, [isValidJson]);

	useEffect(() => {
		if (needDeleteFile) {
			setFileName('');
			setFile('');
			setFullFile(null);
		}
	}, [needDeleteFile]);

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0] && e.target.files[0].type === 'application/json') {
			const fileReader = new FileReader();
			setFileName(e.target.files[0].name);
			setFullFile(e.target.files[0]);
			fileReader.readAsText(e.target.files[0]);
			fileReader.onload = (e) => {
				try {
					const loadedJsonFile = e.target?.result as string;
					const jsonString = JSON.stringify(JSON.parse(loadedJsonFile));
					setFile(jsonString);
				} catch (err) {
					const error = err as Error;
					const appError = createAppError(error);
					addAppError(appError);
				}
			};
		} else {
			const appError = createAppError({ message: t('errorAddingFile'), name: '', stack: '' });
			addAppError(appError);
		}
	};

	// Clear input value to have ability to upload the same files
	const handleResetFile = (e: React.MouseEvent<HTMLInputElement>) => {
		e.currentTarget.value = '';
	};

	const showMessage = () => {
		if (!isValidJson) {
			return null;
		}
		if (requiredValidationMessage) {
			return (
				<Text
					className={style.requiredValidationError}
					translationNameSpace="error"
					translationKey={requiredValidationMessage}
					theme="error"
				/>
			);
		}
		return fileName ? <div className={style.fileName}>{fileName}</div> : null;
	};

	return (
		<div className={style.container} data-test="JSONUploader">
			<input
				className={style.input}
				onClick={handleResetFile}
				onChange={handleChange}
				id={id}
				type="file"
				accept="application/json"
			/>
			<label htmlFor={id} style={{ width: `${width}px` }}>
				<div className={style.loader}>
					<div className={style.label}>
						{icon}
						<Text translationNameSpace="form" translationKey={label} asFragment={true} />
					</div>
				</div>
			</label>
			{showMessage()}
		</div>
	);
};

export default React.memo(JSONUploader);
